import { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Download, Droplets, Plus, Trash2 } from "lucide-react";

import { ApiError, formatDate } from "../../lib/api";
import { useToast } from "../../components/Toast";
import { Modal } from "../../components/Modal";
import {
  Button,
  Cargando,
  ErrorCarga,
  Input,
  TableWrap,
  Textarea,
  useDescarteProtegido,
  useHayCambios,
} from "../../components/ui";
import {
  crearProduccion,
  descargarCsvProduccion,
  fetchProduccion,
  type FuenteProduccion,
  type ProduccionCreateInput,
} from "./api";

function defaultRange(): { desde: string; hasta: string } {
  const hoy = new Date();
  const desde = new Date(hoy.getFullYear(), hoy.getMonth(), 1);
  return {
    desde: desde.toISOString().slice(0, 10),
    hasta: hoy.toISOString().slice(0, 10),
  };
}

export function ProduccionDiariaSection(): JSX.Element {
  const toast = useToast();
  const qc = useQueryClient();
  const [{ desde, hasta }, setRange] = useState(defaultRange);
  const [crearAbierto, setCrearAbierto] = useState(false);

  const listQ = useQuery({
    queryKey: ["produccion", desde, hasta],
    queryFn: () => fetchProduccion(desde, hasta),
  });

  const exportMut = useMutation({
    mutationFn: () => descargarCsvProduccion(desde, hasta),
    onSuccess: () => toast.success("CSV descargado"),
    onError: () => toast.error("No se pudo exportar"),
  });

  const items = listQ.data?.items ?? [];

  const totalPeriodo = useMemo(() => {
    return items.reduce((acc, it) => acc + Number(it.total_m3 || 0), 0);
  }, [items]);

  return (
    <div className="space-y-3 pt-6">
      <div className="flex items-center justify-between">
        <h2 className="text-base font-semibold text-neutral-800">
          Producción diaria de agua
        </h2>
        <div className="flex gap-2">
          <Button
            variant="secondary"
            icon={Download}
            onClick={() => exportMut.mutate()}
            disabled={exportMut.isPending}
          >
            CSV (SISS)
          </Button>
          <Button icon={Plus} onClick={() => setCrearAbierto(true)}>
            Registrar día
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        <Field label="Desde">
          <input
            type="date"
            value={desde}
            onChange={(e) =>
              setRange((s) => ({ ...s, desde: e.target.value }))
            }
            className="w-full rounded-md border border-neutral-450 px-2 py-1.5 text-sm"
          />
        </Field>
        <Field label="Hasta">
          <input
            type="date"
            value={hasta}
            onChange={(e) =>
              setRange((s) => ({ ...s, hasta: e.target.value }))
            }
            className="w-full rounded-md border border-neutral-450 px-2 py-1.5 text-sm"
          />
        </Field>
        <div className="col-span-2 flex items-end justify-end pb-1 text-sm text-neutral-500">
          Total del periodo:{" "}
          <span className="ml-1 font-semibold text-neutral-900">
            {totalPeriodo.toLocaleString("es-CL")} m³
          </span>
        </div>
      </div>

      {listQ.isPending ? (
        <Cargando />
      ) : listQ.isError ? (
        <ErrorCarga error={listQ.error} onReintentar={() => listQ.refetch()} />
      ) : items.length === 0 ? (
        <p className="text-sm text-neutral-500">
          Sin registros en este rango. Registra un día para empezar.
        </p>
      ) : (
        <TableWrap label="Producción diaria">
          <table className="min-w-full divide-y divide-neutral-200 text-sm">
            <thead className="bg-neutral-50 text-xs uppercase tracking-wider text-neutral-500">
              <tr>
                <th scope="col" className="px-3 py-2 text-left">Fecha</th>
                <th scope="col" className="px-3 py-2 text-right">Total m³</th>
                <th scope="col" className="px-3 py-2 text-left">Detalle</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-neutral-100">
              {items.map((p) => (
                <tr key={p.id} className="hover:bg-neutral-50">
                  <td className="px-3 py-2 whitespace-nowrap">
                    {formatDate(p.fecha)}
                  </td>
                  <td className="px-3 py-2 text-right tabular-nums">
                    {Number(p.total_m3).toLocaleString("es-CL")}
                  </td>
                  <td className="px-3 py-2 text-neutral-600 text-xs">
                    {p.detalle_fuentes
                      .map((f) =>
                        f.horas
                          ? `${f.fuente}: ${f.m3}m³ (${f.horas}h)`
                          : `${f.fuente}: ${f.m3}m³`,
                      )
                      .join(" · ")}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </TableWrap>
      )}

      {crearAbierto && (
        <CrearProduccionModal
          onClose={() => setCrearAbierto(false)}
          onCreated={() => {
            setCrearAbierto(false);
            qc.invalidateQueries({ queryKey: ["produccion"] });
            toast.success("Producción registrada");
          }}
        />
      )}
    </div>
  );
}

function CrearProduccionModal({
  onClose,
  onCreated,
}: {
  onClose: () => void;
  onCreated: () => void;
}): JSX.Element {
  const toast = useToast();
  const [fecha, setFecha] = useState(new Date().toISOString().slice(0, 10));
  const [fuentes, setFuentes] = useState<FuenteProduccion[]>([
    { fuente: "Pozo 1", tipo: "pozo", m3: "0", horas: null },
  ]);
  const [observaciones, setObservaciones] = useState("");

  const mut = useMutation({
    mutationFn: (input: ProduccionCreateInput) => crearProduccion(input),
    onSuccess: () => onCreated(),
    onError: (err: unknown) => {
      const msg = err instanceof ApiError ? err.message : "No se pudo guardar";
      toast.error(msg);
    },
  });

  function setFuente(i: number, patch: Partial<FuenteProduccion>): void {
    setFuentes((prev) => {
      const next = [...prev];
      next[i] = { ...next[i], ...patch } as FuenteProduccion;
      return next;
    });
  }

  function agregarFuente(): void {
    setFuentes((prev) => [
      ...prev,
      { fuente: "", tipo: "pozo", m3: "0", horas: null },
    ]);
  }

  function quitarFuente(i: number): void {
    setFuentes((prev) => prev.filter((_, idx) => idx !== i));
  }

  function onSubmit(e: React.FormEvent): void {
    e.preventDefault();
    if (fuentes.length === 0) {
      toast.error("Agrega al menos una fuente");
      return;
    }
    mut.mutate({
      fecha,
      detalle_fuentes: fuentes.map((f) => ({
        fuente: f.fuente.trim() || "Sin nombre",
        tipo: f.tipo,
        m3: f.m3 || "0",
        horas: f.horas || null,
      })),
      observaciones: observaciones.trim() || null,
    });
  }

  // Cambios sin guardar: cerrar pregunta antes de descartar (auditoría UX-01).
  const hayCambios = useHayCambios([fecha, fuentes, observaciones]);
  const { cerrar: intentarCerrar, dialogo: dialogoDescarte } = useDescarteProtegido(
    hayCambios,
    onClose,
    mut.isPending,
  );

  return (
    <Modal
      open
      onClose={intentarCerrar}
      title="Registrar producción de un día"
      icon={Droplets}
      size="lg"
      footer={
        <>
          <Button variant="secondary" onClick={intentarCerrar} disabled={mut.isPending}>
            Cancelar
          </Button>
          <Button type="submit" form="crear-produccion-form" disabled={mut.isPending}>
            {mut.isPending ? "Guardando…" : "Registrar"}
          </Button>
        </>
      }
    >
      <form id="crear-produccion-form" onSubmit={onSubmit} className="space-y-3">
        <Input
          label="Fecha"
          required
          type="date"
          value={fecha}
          onChange={(e) => setFecha(e.target.value)}
        />
        <div>
          <div className="mb-1 flex items-center justify-between">
            <label className="text-xs font-medium text-neutral-600">
              Fuentes de producción
            </label>
            <Button variant="link" size="sm" icon={Plus} onClick={agregarFuente}>
              Agregar fuente
            </Button>
          </div>
          <div className="space-y-2">
            {fuentes.map((f, i) => (
              <div
                key={i}
                className="grid grid-cols-12 items-center gap-2 rounded-md bg-neutral-50 p-2"
              >
                <input
                  placeholder="Nombre (ej: Pozo 1)"
                  value={f.fuente}
                  onChange={(e) => setFuente(i, { fuente: e.target.value })}
                  className="col-span-4 rounded-md border border-neutral-450 px-2 py-1 text-sm"
                />
                <select
                  value={f.tipo}
                  onChange={(e) =>
                    setFuente(i, {
                      tipo: e.target.value as FuenteProduccion["tipo"],
                    })
                  }
                  className="col-span-2 rounded-md border border-neutral-450 px-2 py-1 text-sm"
                >
                  <option value="pozo">Pozo</option>
                  <option value="aljibe">Aljibe</option>
                  <option value="otro">Otro</option>
                </select>
                <input
                  type="number"
                  step="0.01"
                  min={0}
                  placeholder="m³"
                  value={f.m3}
                  onChange={(e) => setFuente(i, { m3: e.target.value })}
                  className="col-span-3 rounded-md border border-neutral-450 px-2 py-1 text-sm"
                />
                <input
                  type="number"
                  step="0.1"
                  min={0}
                  max={24}
                  placeholder="horas"
                  value={f.horas ?? ""}
                  onChange={(e) =>
                    setFuente(i, { horas: e.target.value || null })
                  }
                  className="col-span-2 rounded-md border border-neutral-450 px-2 py-1 text-sm"
                />
                <div className="col-span-1 flex justify-end">
                  <Button
                    variant="linkDanger"
                    size="sm"
                    icon={Trash2}
                    iconOnly
                    aria-label="Quitar fuente"
                    onClick={() => quitarFuente(i)}
                  />
                </div>
              </div>
            ))}
          </div>
        </div>
        <Textarea
          label="Observaciones"
          value={observaciones}
          onChange={(e) => setObservaciones(e.target.value)}
          rows={2}
        />
      </form>
      {dialogoDescarte}
    </Modal>
  );
}

function Field({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}): JSX.Element {
  return (
    <div>
      <label className="mb-1 block text-xs font-medium text-neutral-600">
        {label}
      </label>
      {children}
    </div>
  );
}
